import { computeSCurveGeometry, computeTimeTicks, computeValueAxis, toPolylinePoints } from '../resource/graph-geometry.js';
import { formatMoney, formatRatio } from '../resource/format.js';
import type { EvmReportProps } from './types.js';

const MARGIN = { left: 72, right: 8, top: 8, bottom: 20 };

/** `YYYY-MM-DD` → `MM-DD`，與其他視圖的日期標籤慣例一致。 */
function formatAxisDate(ms: number): string {
  return new Date(ms).toISOString().slice(5, 10);
}

/**
 * EVM 報表：指標卡片 + S 曲線 + EAC 方法比較。純展示元件，第 3 層引擎的門面。
 *
 * 狀態判斷順序（與 cost-evm engine 對齊）：
 * 1. `diagnostics` 含 `EVM_NO_BASELINE`（Warning）→ 顯示「尚未設定基準線」引導語，
 *    取代整片 N/A／0，因為這是使用者需要採取行動的狀態。
 * 2. 否則依 spi/cpi/tcpi 是否為 null 顯示「N/A」（分母為 0 的計算邊界，不需使用者
 *    採取行動；EVM_ZERO_PV / EVM_ZERO_AC / EVM_ZERO_TCPI_DENOMINATOR 可用於更精確
 *    的文案，但目前先用同一句「N/A」呈現，程度已足夠區分「有問題」與「無資料」）。
 *
 * 一律用 `code` 判斷，不用 `message`（message 是英文 fallback，無 i18n key）。
 */

interface MetricCardDef {
  readonly key: keyof EvmReportProps['metrics'];
  readonly label: string;
  readonly kind: 'money' | 'ratio';
}

const METRIC_CARDS: readonly MetricCardDef[] = [
  { key: 'pv', label: 'PV（計畫值）', kind: 'money' },
  { key: 'ev', label: 'EV（實獲值）', kind: 'money' },
  { key: 'ac', label: 'AC（實際成本）', kind: 'money' },
  { key: 'sv', label: 'SV（時程差異）', kind: 'money' },
  { key: 'cv', label: 'CV（成本差異）', kind: 'money' },
  { key: 'spi', label: 'SPI（時程績效指數）', kind: 'ratio' },
  { key: 'cpi', label: 'CPI（成本績效指數）', kind: 'ratio' },
  { key: 'bac', label: 'BAC（完工預算）', kind: 'money' },
  { key: 'eac', label: 'EAC（完工估算）', kind: 'money' },
  { key: 'vac', label: 'VAC（完工差異）', kind: 'money' },
  { key: 'tcpi', label: 'TCPI（完工績效指數）', kind: 'ratio' },
];

const EAC_METHOD_LABEL: Record<string, string> = {
  Additive: '加法法（依計畫執行）',
  CpiBased: 'CPI 法（維持目前績效）',
  CpiSpiBased: 'CPI×SPI 法（同時考量時程）',
};

function statusColorClass(value: number | null): string {
  if (value === null) return 'text-fg-muted';
  return value >= 1 ? 'text-emerald-600 dark:text-ok' : 'text-red-600 dark:text-danger';
}

export function EvmReport(props: EvmReportProps): React.JSX.Element {
  const { metrics, currency, statusDate, curve, eacComparison, diagnostics, width = 640, height = 220 } = props;

  const hasNoBaseline = diagnostics.some((d) => d.code === 'EVM_NO_BASELINE');

  if (hasNoBaseline) {
    return (
      <div
        data-testid="evm-report"
        data-state="no-baseline"
        className="flex h-full w-full flex-col items-center justify-center gap-2 bg-surface p-8 text-center text-sm text-fg-muted"
      >
        <div data-testid="evm-no-baseline-message" className="text-base font-medium">
          尚未設定基準線
        </div>
        <div className="max-w-md text-fg-muted">
          實獲值分析（EVM）需要先建立基準線才能計算 PV／EV／SPI／CPI 等指標。
          請先在專案中設定基準線，再回到此報表查看結果。
        </div>
      </div>
    );
  }

  const plotWidth = Math.max(1, width - MARGIN.left - MARGIN.right);
  const plotHeight = Math.max(1, height - MARGIN.top - MARGIN.bottom);
  const dataMax = curve.reduce((m, p) => Math.max(m, p.pv, p.ev, p.ac), 0);
  const yAxis = computeValueAxis(dataMax, 4);
  const geometry = computeSCurveGeometry(
    curve,
    statusDate,
    plotWidth,
    plotHeight,
    yAxis.niceMax > 0 ? yAxis.niceMax : undefined,
  );
  const first = curve[0];
  const last = curve[curve.length - 1];
  // `Date.parse()` 對非法日期字串回傳 `NaN`，不是 `null`——用 `Number.isNaN()` 檢查，
  // 否則非法值會直接流進 computeTimeTicks() 產出 NaN 座標。
  const t0 = first !== undefined ? Date.parse(first.date) : NaN;
  const t1 = last !== undefined ? Date.parse(last.date) : NaN;
  const xTicks = !Number.isNaN(t0) && !Number.isNaN(t1) ? computeTimeTicks(t0, t1, plotWidth) : [];

  return (
    <div data-testid="evm-report" data-state="ready" className="flex h-full w-full flex-col gap-4 overflow-auto bg-surface p-3 text-sm text-fg">
      <div className="grid grid-cols-4 gap-2" data-testid="evm-metric-cards">
        {METRIC_CARDS.map((card) => {
          const value = metrics[card.key];
          const display = card.kind === 'money' ? formatMoney(value as number, currency) : formatRatio(value as number | null);
          const colorClass = card.kind === 'ratio' ? statusColorClass(value as number | null) : '';
          return (
            <div
              key={card.key}
              data-testid={`evm-metric-card-${card.key}`}
              className="rounded border border-line p-2"
            >
              <div className="text-xs text-fg-muted">{card.label}</div>
              <div data-testid={`evm-metric-value-${card.key}`} className={['text-base font-semibold', colorClass].join(' ')}>
                {display}
              </div>
            </div>
          );
        })}
      </div>

      <div>
        <div className="mb-1 font-medium">S 曲線</div>
        <svg
          role="img"
          aria-label="EVM S 曲線"
          data-testid="evm-scurve"
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          className="overflow-visible"
        >
          {/* Y 軸刻度、格線與金額標籤 */}
          {yAxis.ticks.map((tick) => {
            const y = MARGIN.top + plotHeight - tick.ratio * plotHeight;
            return (
              <g key={tick.value} data-testid={`evm-scurve-ytick-${tick.value}`}>
                <line
                  x1={MARGIN.left}
                  x2={MARGIN.left + plotWidth}
                  y1={y}
                  y2={y}
                  className="stroke-hairline"
                  strokeWidth={1}
                />
                <text
                  x={MARGIN.left - 6}
                  y={y}
                  dominantBaseline="middle"
                  textAnchor="end"
                  className="fill-fg-muted text-[10px]"
                >
                  {formatMoney(tick.value, currency)}
                </text>
              </g>
            );
          })}

          {/* X 軸時間刻度與日期標籤（已依 targetTickCount 防重疊） */}
          {xTicks.map((tick) => (
            <g key={tick.ms} data-testid={`evm-scurve-xtick-${tick.ms}`}>
              <text
                x={MARGIN.left + tick.x}
                y={MARGIN.top + plotHeight + 14}
                textAnchor="middle"
                className="fill-fg-muted text-[10px]"
              >
                {formatAxisDate(tick.ms)}
              </text>
            </g>
          ))}

          <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
            <polyline data-testid="evm-scurve-pv" points={toPolylinePoints(geometry.pv)} fill="none" className="stroke-fg-muted" strokeWidth={2} />
            <polyline data-testid="evm-scurve-ev" points={toPolylinePoints(geometry.ev)} fill="none" className="stroke-ok" strokeWidth={2} />
            <polyline data-testid="evm-scurve-ac" points={toPolylinePoints(geometry.ac)} fill="none" className="stroke-danger" strokeWidth={2} />
            {geometry.statusDateX !== null ? (
              <g>
                <line
                  data-testid="evm-scurve-status-line"
                  x1={geometry.statusDateX}
                  x2={geometry.statusDateX}
                  y1={0}
                  y2={plotHeight}
                  className="stroke-focus"
                  strokeDasharray="4 2"
                />
                <text
                  data-testid="evm-scurve-status-label"
                  x={geometry.statusDateX}
                  y={-2}
                  textAnchor="middle"
                  className="fill-focus text-[10px]"
                >
                  狀態日 {statusDate.slice(5, 10)}
                </text>
              </g>
            ) : null}
          </g>

          {/* 軸線 */}
          <line
            x1={MARGIN.left}
            x2={MARGIN.left}
            y1={MARGIN.top}
            y2={MARGIN.top + plotHeight}
            className="stroke-line-strong"
          />
          <line
            x1={MARGIN.left}
            x2={MARGIN.left + plotWidth}
            y1={MARGIN.top + plotHeight}
            y2={MARGIN.top + plotHeight}
            className="stroke-line-strong"
          />
        </svg>
        <div className="mt-1 flex gap-3 text-xs">
          <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-fg-muted" />PV</span>
          <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-ok" />EV</span>
          <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-danger" />AC</span>
        </div>
      </div>

      <div>
        <div className="mb-1 font-medium">EAC 方法比較</div>
        <div className="flex gap-4" data-testid="evm-eac-comparison">
          {eacComparison.map((entry) => (
            <div key={entry.method} data-testid={`evm-eac-${entry.method}`} className="rounded border border-line p-2">
              <div className="text-xs text-fg-muted">{EAC_METHOD_LABEL[entry.method] ?? entry.method}</div>
              <div className="text-base font-semibold">{formatMoney(entry.eac, currency)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default EvmReport;
